import { Device, RepairOption, Order, ApiResponse, PaymentIntent } from '../types';

// Use environment variable for API URL, fallback to Railway production
const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://phonemedic-production.up.railway.app/api';

class ApiService {
  private fetchWithErrorHandling = async <T>(
    url: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> => {
    try {
      const response = await fetch(`${API_BASE_URL}${url}`, {
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
        ...options,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    }
  }

  // Device API methods
  getDevices = async (): Promise<ApiResponse<Record<string, Device[]>>> => {
    return this.fetchWithErrorHandling<Record<string, Device[]>>('/devices');
  }

  getDevicesByBrand = async (brand: string): Promise<ApiResponse<Device[]>> => {
    return this.fetchWithErrorHandling<Device[]>(`/devices/${encodeURIComponent(brand)}`);
  }

  getDeviceById = async (id: string): Promise<ApiResponse<{ device: Device; repairs: RepairOption[] }>> => {
    return this.fetchWithErrorHandling<{ device: Device; repairs: RepairOption[] }>(`/devices/${id}/repairs`);
  }

  // Repair API methods
  getRepairs = async (): Promise<ApiResponse<RepairOption[]>> => {
    return this.fetchWithErrorHandling<RepairOption[]>('/repairs');
  }

  getRepairsByDevice = async (deviceId: string): Promise<ApiResponse<{ device: Device; repairs: RepairOption[] }>> => {
    return this.fetchWithErrorHandling<{ device: Device; repairs: RepairOption[] }>(`/devices/${deviceId}/repairs`);
  }

  getRepairById = async (id: string): Promise<ApiResponse<RepairOption>> => {
    return this.fetchWithErrorHandling<RepairOption>(`/repairs/${id}`);
  }

  // Order API methods
  createOrder = async (orderData: {
    customerInfo: {
      firstName: string;
      lastName: string;
      email: string;
      phone: string;
    };
    shippingAddress: {
      street: string;
      city: string;
      postalCode: string;
      country: string;
    };
    items: Array<{
      repairId: string;
      deviceId: string;
      title: string;
      price: number;
      isExpress: boolean;
      quantity: number;
    }>;
  }): Promise<ApiResponse<Order>> => {
    return this.fetchWithErrorHandling<Order>('/orders', {
      method: 'POST',
      body: JSON.stringify(orderData),
    });
  }

  getOrder = async (orderNumber: string): Promise<ApiResponse<Order>> => {
    return this.fetchWithErrorHandling<Order>(`/orders/${orderNumber}`);
  }

  // Payment API methods
  createPaymentIntent = async (paymentData: {
    amount: number;
    currency: string;
    orderId: string;
  }): Promise<ApiResponse<PaymentIntent>> => {
    return this.fetchWithErrorHandling<PaymentIntent>('/payment/create-intent', {
      method: 'POST',
      body: JSON.stringify(paymentData),
    });
  }

  confirmPayment = async (paymentIntentId: string): Promise<ApiResponse<Order>> => {
    return this.fetchWithErrorHandling<Order>('/payment/success', {
      method: 'POST',
      body: JSON.stringify({ paymentIntentId }),
    });
  }

  // User API methods
  register = async (userData: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
  }): Promise<ApiResponse<{ user: any; token: string }>> => {
    return this.fetchWithErrorHandling<{ user: any; token: string }>('/users/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  login = async (credentials: {
    email: string;
    password: string;
  }): Promise<ApiResponse<{ user: any; token: string }>> => {
    return this.fetchWithErrorHandling<{ user: any; token: string }>('/users/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
  }
}

export default new ApiService();